import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import Pagination from '../../components/common/Pagination';
import StyledSelectDropdown from '../../components/common/StyledSelectDropdown';
import { FiSearch, FiUser, FiPlus } from 'react-icons/fi';
import { PRIMARY } from '../../theme';
import { can, PERMISSIONS, getPeopleBasePath } from '../../utils/permissions';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import '../../styles/Table.css';

// Roles that can ever be a manager (mirrors AddPersonPage) — used to populate
// the Manager filter dropdown with only manager-eligible users.
const MANAGER_ELIGIBLE_ROLES = ['admin', 'hod', 'manager', 'team-leader'];

// Toolbar styling tokens (match Teams module)
const PEOPLE_PURPLE = 'rgb(102, 126, 234)';
const TOOLBAR_BORDER = '#dee2e6';

const PeopleListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const peopleBase = getPeopleBasePath(location.pathname);
  const canAddEmployee = can(PERMISSIONS.ADD_EMPLOYEE);
  const canEditEmployee = can(PERMISSIONS.EDIT_EMPLOYEE);
  const canDeleteEmployee = can(PERMISSIONS.DELETE_EMPLOYEE);
  const canManageEmployeePermissions = can(PERMISSIONS.MANAGE_EMPLOYEE_PERMISSIONS);
  const showRowSelection =
    canAddEmployee || canEditEmployee || canDeleteEmployee || canManageEmployeePermissions;
  const showActionColumn =
    canAddEmployee || canEditEmployee || canDeleteEmployee || canManageEmployeePermissions;
  const [loading, setLoading] = useState(false);
  const [people, setPeople] = useState([]);
  const [totalPeople, setTotalPeople] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());

  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);
  
  // Use ref for search to prevent re-renders
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    fetchSitesAndDepartments();
  }, []);

  // Fetch people when search or pagination changes - all handled by backend
  useEffect(() => {
    fetchPeople();
  }, [currentPage, itemsPerPage, searchTerm, selectedManager, sortBy, sortOrder]);
  
  // Handle search with debouncing - NO state update until debounce completes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    
    // Clear previous timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    // Set new timer
    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 500);
  };

  const fetchPeople = async () => {
    try {
      // DON'T set loading to true - it causes re-render and focus loss
      // setLoading(true);

      // Build query parameters - all filtering and pagination handled by backend
      // Do not filter by role here so both employees and admins are visible
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add search term if provided
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add manager filter if selected
      if (selectedManager) {
        params.manager = selectedManager;
      }

      // Add sorting if selected
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }

      const response = await api.getUsers(params);
      if (response.success) {
        // Save the currently focused element
        const activeElement = document.activeElement;
        const wasSearchFocused = activeElement === searchInputRef.current;
        
        setPeople(response.data || []);
        setTotalPeople(response.total || 0);
        setTotalPages(response.totalPages || 1);
        
        // Restore focus if it was on the search input
        if (wasSearchFocused && searchInputRef.current) {
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      }
    } catch (error) {
      console.error('Error fetching people:', error);
      toast.error('Error fetching people');
    } finally {
      setLoading(false);
    }
  };

  const fetchSitesAndDepartments = async () => {
    try {
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }

      // Manager filter options — only manager-eligible users (high limit so
      // older users aren't dropped by the default 25-per-page cap).
      const managersRes = await api.getUsers({
        role: MANAGER_ELIGIBLE_ROLES.join(','),
        limit: 1000,
      });
      if (managersRes.success) {
        setManagers(managersRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching sites/departments:', error);
    }
  };

  const handleSelectPerson = (personId) => {
    setSelectedPeople(prev => {
      if (prev.includes(personId)) {
        return prev.filter(id => id !== personId);
      } else {
        return [...prev, personId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPeople.length === people.length) {
      setSelectedPeople([]);
    } else {
      setSelectedPeople(people.map(p => p.id || p._id));
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Teams-style sort arrows: idle = up/down (dim), active = up or down (highlighted)
  const getSortIcon = (field) => {
    if (sortBy !== field) return '\u2195'; // up-down
    return sortOrder === 'asc' ? '\u2191' : '\u2193';
  };

  const sortableLabel = (field, label) => {
    const isActive = sortBy === field;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <span>{label}</span>
        <span style={{ color: isActive ? '#e9d5ff' : 'rgba(255,255,255,0.65)', fontSize: '10px' }}>
          {getSortIcon(field)}
        </span>
      </span>
    );
  };



  const handleEdit = (personId) => {
    navigate(`${peopleBase}/${personId}/edit`);
  };

  const handleDelete = async (personId) => {
    if (window.confirm('Are you sure you want to delete this person? This action cannot be undone.')) {
      try {
        await api.deleteUser(personId);
        toast.success('Person deleted successfully');
        fetchPeople();
      } catch (error) {
        console.error('Error deleting person:', error);
        toast.error('Error deleting person: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const handleViewProfile = (personId) => {
    navigate(`${peopleBase}/${personId}`);
  };

  const handlePermissions = (personId) => {
    navigate(`${peopleBase}/${personId}/permissions`);
  };

  const getSiteName = (site) => {
    if (!site) return 'N/A';
    // If site is already an object with name property (populated)
    if (typeof site === 'object' && site.name) {
      return site.name;
    }
    // If site is an ID, look it up
    const siteObj = sites.find(s => (s.id || s._id) === site || (s.id || s._id) === (site._id || site.id));
    return siteObj?.name || 'N/A';
  };

  const getManagerName = (manager) => {
    if (!manager) return 'N/A';
    // If manager is already a populated object with a name
    if (typeof manager === 'object' && manager.name) {
      return manager.name;
    }
    // If manager is an ID, try to find it among the loaded people
    const managerId = manager._id || manager.id || manager;
    const managerObj = people.find(p => (p.id || p._id) === managerId);
    return managerObj?.name || 'N/A';
  };

  const getDepartmentName = (dept) => {
    if (!dept) return 'N/A';
    // If department is already an object with name property (populated)
    if (typeof dept === 'object' && dept.name) {
      return dept.name;
    }
    // If department is an ID, look it up
    const deptObj = departments.find(d => (d.id || d._id) === dept || (d.id || d._id) === (dept._id || dept.id));
    return deptObj?.name || 'N/A';
  };

  if (loading && people.length === 0) {
    return <Loader />;
  }

  return (
    <div className="people-list-page" style={{ padding: '16px' }}>
      {/* Toolbar: filters (left) + Add Person (right) on one line */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '16px'
      }}>
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <div style={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            columnGap: 12,
            width: '100%'
          }}>
            {/* Left group: search + manager filter */}
            <div style={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 8,
              flex: '1 1 auto',
              minWidth: 0
            }}>
              <div style={{ position: 'relative', flex: '0 0 220px', minWidth: 0 }}>
                <FiSearch
                  size={14}
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#98a2b3',
                    pointerEvents: 'none'
                  }}
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search employees by name, email, ID, phone, or title..."
                  defaultValue={searchTerm}
                  onChange={handleSearchChange}
                  autoComplete="off"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    height: 34,
                    padding: '0 10px 0 32px',
                    border: `1px solid ${TOOLBAR_BORDER}`,
                    borderRadius: 6,
                    fontSize: 12,
                    color: '#344054',
                    background: '#fff'
                  }}
                />
              </div>
              <div style={{ flex: '0 0 200px', minWidth: 0 }}>
                <StyledSelectDropdown
                  ariaLabel="Filter by manager"
                  placeholder="All Managers"
                  emptyOptionLabel="All Managers"
                  value={selectedManager}
                  onChange={(v) => {
                    setSelectedManager(v);
                    setCurrentPage(1);
                  }}
                  options={managers.map((m) => ({
                    value: m.id || m._id,
                    label: m.name
                  }))}
                  leftIcon={<FiUser size={14} style={{ color: PEOPLE_PURPLE, flexShrink: 0 }} aria-hidden />}
                />
              </div>
            </div>
            {canAddEmployee && (
              <button
                type="button"
                onClick={() => navigate(`${peopleBase}/add`)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  flexShrink: 0,
                  height: 34,
                  padding: '0 14px',
                  border: 'none',
                  color: '#ffffff',
                  background: PRIMARY,
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 1px 2px rgba(78, 115, 223, 0.28)'
                }}
              >
                <FiPlus size={15} strokeWidth={2.5} aria-hidden style={{ color: '#fff' }} />
                Add Person
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {people.length === 0 && !loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
            <p style={{ margin: 0, fontSize: '14px' }}>No employees found</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: PRIMARY }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', width: '50px' }}>S.No</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('name')}
                    >{sortableLabel('name', 'Name')}</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('employeeId')}
                    >{sortableLabel('employeeId', 'Employee ID')}</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('title')}
                    >{sortableLabel('title', 'Title')}</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('jobType')}
                    >{sortableLabel('jobType', 'Job Type')}</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('role')}
                    >{sortableLabel('role', 'Role')}</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('manager')}
                    >{sortableLabel('manager', 'Manager')}</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('phone')}
                    >{sortableLabel('phone', 'Phone')}</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap' }}>Site</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap' }}>Location</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap' }}>Department</th>
                    {showActionColumn && (
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', width: '180px' }}>Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {people.map((person, index) => {
                    const personId = person.id || person._id;
                    const isSelected = selectedPeople.includes(personId);
                    return (
                      <tr key={personId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px', verticalAlign: 'middle', whiteSpace: 'nowrap', color: '#4a5568', fontSize: '13px' }}>
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td style={{ padding: '10px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => handleViewProfile(personId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#667eea',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: '13px',
                              textDecoration: 'underline',
                              padding: 0,
                              textAlign: 'left',
                              display: 'inline-block'
                            }}
                          >
                            {person.name || 'N/A'}
                          </button>
                        </td>
                        <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {person.employeeId || 'N/A'}
                        </td>
                        <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {person.title || 'N/A'}
                        </td>
                        <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {person.jobType || 'N/A'}
                        </td>
                        <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {person.role ? person.role.charAt(0).toUpperCase() + person.role.slice(1) : 'N/A'}
                        </td>
                        <td style={{ padding: '10px', color: '#718096', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {getManagerName(person.manager)}
                        </td>
                        <td style={{ padding: '10px', color: '#718096', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {person.phone || 'N/A'}
                        </td>
                        <td style={{ padding: '10px', color: '#718096', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {person.email || 'N/A'}
                        </td>
                        <td style={{ padding: '10px', color: '#718096', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {getSiteName(person.site)}
                        </td>
                        <td style={{ padding: '10px', color: '#718096', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {person.location || 'N/A'}
                        </td>
                        <td style={{ padding: '10px', color: '#718096', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {getDepartmentName(person.department)}
                        </td>
                        {showActionColumn && (
                        <td style={{ padding: '10px', textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            {canEditEmployee && (
                            <button
                              onClick={() => handleEdit(personId)}
                              style={{
                                padding: '6px 12px',
                                background: '#e6fffa',
                                color: '#234e52',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 500
                              }}
                              title="Edit"
                            >
                              ✏️
                            </button>
                            )}
                            {canManageEmployeePermissions && (
                            <button
                              onClick={() => handlePermissions(personId)}
                              style={{
                                padding: '6px 12px',
                                background: '#ebf8ff',
                                color: '#2a4365',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 500
                              }}
                              title="Permissions"
                            >
                              🔐
                            </button>
                            )}
                            {canDeleteEmployee && (
                            <button
                              onClick={() => handleDelete(personId)}
                              style={{
                                padding: '6px 12px',
                                background: '#fed7d7',
                                color: '#c53030',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 500
                              }}
                              title="Delete"
                            >
                              🗑️
                            </button>
                            )}
                          </div>
                        </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalPeople}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PeopleListPage;
